import { useContext, useEffect, useRef, useState } from "react";
import { AppStateContext } from "../state/AppProvider";
import { getAccessToken } from "../utils/token";

interface PowerBIEmbedConfig {
    reportId: string;
    embedUrl: string;
    accessToken: string;
}

const getTokenRefreshTime = (accessToken: string): number | null => {
    try {
        const payload = accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const decodedPayload = atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, "="));
        const tokenBytes = Uint8Array.from(decodedPayload, character => character.charCodeAt(0));
        const tokenClaims = JSON.parse(new TextDecoder().decode(tokenBytes));
        return typeof tokenClaims.exp === "number" ? Math.max(Date.now() + 1_000, tokenClaims.exp * 1000 - 120_000) : null;
    } catch {
        return null;
    }
};

// App-owns-data: the backend (via the service principal) generates the embed
// token, with the RLS identity resolved from whichever program is currently
// active — so the token itself, not just the data displayed, is per-program.
// Access is gated server-side on every token request (PriyaPBIAPI re-verifies
// independently of what the UI shows); this hook's hasPowerBIAccess is only a
// coarse check for whether the icon is worth showing in the first place.
export const usePowerBIEmbed = (configCode: string) => {
    const appStateContext = useContext(AppStateContext);
    const { msalInstance, b2cAuthResponse } = appStateContext?.state || {};
    const showPowerBI = appStateContext?.state.frontendSettings?.branding?.show_pbi === "Y";

    // Mirrors configCode without going stale inside async callbacks that may
    // resolve after a later render already moved configCode on.
    const configCodeRef = useRef(configCode);
    configCodeRef.current = configCode;

    const [isPowerBIOverlayOpen, setIsPowerBIOverlayOpen] = useState<boolean>(false);
    const [hasOpenedPowerBI, setHasOpenedPowerBI] = useState<boolean>(false);
    const [powerBIIframeLoaded, setPowerBIIframeLoaded] = useState<boolean>(false);
    const [powerBIEmbedConfig, setPowerBIEmbedConfig] = useState<PowerBIEmbedConfig | null>(null);
    const [powerBIError, setPowerBIError] = useState<string | null>(null);
    const [hasPowerBIAccess, setHasPowerBIAccess] = useState<boolean>(false);
    const powerBIReportRef = useRef<any>(null);
    const powerBITriggerRef = useRef<HTMLElement | null>(null);
    const loadedPowerBIConfigCodeRef = useRef<string | null>(null);
    const [powerBITokenRefreshAt, setPowerBITokenRefreshAt] = useState<number | null>(null);

    const fetchPowerBIEmbedConfig = async (requestedConfigCode = configCodeRef.current) => {
        // setAccessToken() only refreshes an *expired* token under the same RLS
        // identity — it's not a supported way to switch identities, so a program
        // switch instead sets a new embedConfig and lets the consumer's key prop
        // force a real remount (a fresh powerbi.embed() call, not a live patch).
        // Reset the loading UI too, since it otherwise stays hidden forever once
        // the first report finishes loading.
        setPowerBIIframeLoaded(false);
        setPowerBIError(null);
        setPowerBIEmbedConfig(null);
        setPowerBITokenRefreshAt(null);
        loadedPowerBIConfigCodeRef.current = null;
        try {
            // PriyaPBIAPI independently re-verifies access before minting a token —
            // it needs this user's own B2C token to do that, same as the page-access
            // check, not just a request PriyaMain vouches for.
            const token = await getAccessToken(msalInstance, appStateContext?.state.frontendSettings);
            const res = await fetch(`/pbi/embed-token?config_code=${encodeURIComponent(requestedConfigCode)}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const body = await res.json();
            if (!res.ok) throw new Error(body?.error || "Failed to load Power BI report");
            if (requestedConfigCode !== configCodeRef.current) return;
            loadedPowerBIConfigCodeRef.current = requestedConfigCode;
            setPowerBIEmbedConfig(body);
            setPowerBITokenRefreshAt(getTokenRefreshTime(body.accessToken));
        } catch (err: any) {
            if (requestedConfigCode === configCodeRef.current) setPowerBIError(err.message);
        }
    };

    const handlePowerBITokenExpired = async () => {
        const requestedConfigCode = configCodeRef.current;
        try {
            const token = await getAccessToken(msalInstance, appStateContext?.state.frontendSettings);
            const res = await fetch(`/pbi/embed-token?config_code=${encodeURIComponent(requestedConfigCode)}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const body = await res.json();
            if (!res.ok) throw new Error(body?.error || "Failed to refresh Power BI token");
            if (requestedConfigCode !== configCodeRef.current) return;
            await powerBIReportRef.current?.setAccessToken(body.accessToken);
            setPowerBITokenRefreshAt(getTokenRefreshTime(body.accessToken));
            console.info("[PowerBI] token refreshed after expiry");
        } catch (err) {
            if (requestedConfigCode === configCodeRef.current) {
                console.warn("[PowerBI] error refreshing token after expiry", err);
            }
        }
    };

    const openPowerBIOverlay = (trigger?: HTMLElement | null) => {
        powerBITriggerRef.current = trigger || document.activeElement as HTMLElement;
        setIsPowerBIOverlayOpen(true);
        if (powerBIError) void fetchPowerBIEmbedConfig();
        setHasOpenedPowerBI(true);
    };
    const handlePowerBIIconClick = (trigger?: HTMLElement | null) => {
        if (isPowerBIOverlayOpen) {
            setIsPowerBIOverlayOpen(false);
            return;
        }
        openPowerBIOverlay(trigger);
    };
    const closePowerBIOverlay = () => {
        setIsPowerBIOverlayOpen(false);
        requestAnimationFrame(() => {
            const isVisible = (element: HTMLElement | null): element is HTMLElement =>
                Boolean(element?.isConnected && element.getClientRects().length);
            const visiblePowerBITrigger = Array.from(document.querySelectorAll<HTMLElement>('[aria-label="Power BI"]'))
                .find(isVisible);
            const focusTarget = (isVisible(powerBITriggerRef.current) && powerBITriggerRef.current)
                || visiblePowerBITrigger
                || document.getElementById("additional-chat-options");
            if (focusTarget && isVisible(focusTarget)) focusTarget.focus();
        });
    };

    const handlePowerBIEmbedLoaded = () => {
        console.info(`[PowerBI Embed] Report ${powerBIEmbedConfig?.reportId} loaded successfully`);
        setPowerBIIframeLoaded(true);
    };
    const handlePowerBIEmbedError = (event: any) => {
        const message = event?.detail?.message || "Failed to load Power BI report";
        console.error("[PowerBI Embed] error", event?.detail);
        setPowerBIError(message);
    };
    const setPowerBIReport = (embeddedReport: any) => {
        powerBIReportRef.current = embeddedReport;
    };

    // Coarse icon-visibility check only — does this person actually have access
    // to the target Power BI workspace? Power BI itself still decides everything
    // about actual report/RLS access once they sign in; this just decides whether
    // the icon is worth showing in the first place. Gated by a single B2C group
    // (PBI_Access), with a per-program extended role or admin-role capability
    // (canViewPowerBI) also granting access on its own. YW_Show_PBI = "N" turns
    // Power BI off for the program regardless of access.
    useEffect(() => {
        if (!showPowerBI) {
            setHasPowerBIAccess(false);
            setIsPowerBIOverlayOpen(false);
            return;
        }
        if (!b2cAuthResponse) return;
        (async () => {
            try {
                const token = await getAccessToken(msalInstance, appStateContext?.state.frontendSettings);
                const res = await fetch("/pbi/page-access", {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                const body = await res.json();
                setHasPowerBIAccess(Boolean(body?.hasAccess));
            } catch (err) {
                console.warn("Power BI access check failed", err);
            }
        })();
    }, [b2cAuthResponse, showPowerBI]);

    // Refetch on first open, and again any time the active program changes while
    // the embed is already mounted — a stale token would keep showing whichever
    // program's data was loaded first.
    useEffect(() => {
        if (!hasOpenedPowerBI) return;
        if (powerBIEmbedConfig && loadedPowerBIConfigCodeRef.current === configCode) return;
        void fetchPowerBIEmbedConfig(configCode);
    }, [hasOpenedPowerBI, configCode]);

    useEffect(() => {
        if (powerBITokenRefreshAt === null) return;
        const timeout = window.setTimeout(
            handlePowerBITokenExpired,
            Math.max(0, powerBITokenRefreshAt - Date.now())
        );
        return () => window.clearTimeout(timeout);
    }, [powerBITokenRefreshAt]);

    return {
        hasPowerBIAccess,
        hasOpenedPowerBI,
        isPowerBIOverlayOpen,
        powerBIIframeLoaded,
        powerBIEmbedConfig,
        powerBIError,
        handlePowerBIIconClick,
        closePowerBIOverlay,
        handlePowerBITokenExpired,
        handlePowerBIEmbedLoaded,
        handlePowerBIEmbedError,
        setPowerBIReport,
    };
};
